import { useState, useEffect } from "react";
import { classifyClalExe, CLAL_KIND, CLAL_KIND_LABEL } from "../parsers/clalArchive";
import { parseXMLFiles } from "../parsers/xmlParser";
import { parseClalApex } from "../parsers/clalApexParser";
import { useHoldingsStore } from "../hooks/useHoldingsStore";
import { useClalHealthStore } from "../hooks/useClalHealthStore";
import { SOURCE_TYPE } from "../utils/dataSource";
import { deriveValueMonth, monthToValueDate } from "../utils/valueMonth";

// לקובץ יש נתונים לטעינה (ולכן דורש חודש-ערך)?
const clalItemHasData = (it) =>
  !!(it.holdings?.funds?.length || it.health?.members?.length || it.life?.members?.length);

const S = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15,23,42,0.7)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999, padding: 20, direction: "rtl", fontFamily: "system-ui, sans-serif",
  },
  modal: {
    background: "white", borderRadius: 24, width: "100%", maxWidth: 760,
    maxHeight: "90vh", display: "flex", flexDirection: "column",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", overflow: "hidden",
  },
  header: {
    padding: "24px 32px", background: "linear-gradient(135deg, #6d28d9 0%, #a78bfa 100%)",
    color: "white", display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  content: { padding: 28, overflowY: "auto", flex: 1, background: "#f8fafc" },
  footer: {
    padding: "20px 32px", background: "white", borderTop: "1px solid #e2e8f0",
    display: "flex", justifyContent: "flex-end", gap: 12,
  },
  card: {
    background: "white", borderRadius: 14, padding: 16, marginBottom: 14,
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0",
  },
  btn: {
    padding: "10px 24px", borderRadius: 12, fontWeight: 600, fontSize: 15,
    cursor: "pointer", transition: "all 0.2s", border: "none",
  },
  btnPrimary: { background: "#6d28d9", color: "white", boxShadow: "0 4px 12px rgba(109,40,217,0.3)" },
  btnSecondary: { background: "#f1f5f9", color: "#475569" },
  badge: (bg, fg) => ({ background: bg, color: fg, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }),
};

// Decode a holdings DAT/XML ArrayBuffer the same way useBatchLoader does.
function decodeXml(arrayBuffer) {
  const sniff = new TextDecoder("utf-8").decode(new Uint8Array(arrayBuffer, 0, 200));
  const m = sniff.match(/encoding=["']([^"']+)["']/i);
  const enc = m ? m[1] : "utf-8";
  return new TextDecoder(enc).decode(arrayBuffer);
}

export default function ClalImport({ initialFiles, onClose }) {
  const { addHoldingsFunds } = useHoldingsStore();
  const { addClalImport } = useClalHealthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]); // per-file: { fileName, kind, holdings?, health?, error? }
  const [months, setMonths] = useState({}); // { [itemIdx]: "YYYY-MM" } חודש-ערך חובה לכל קובץ

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const out = [];
        const filesToProcess = initialFiles ? Array.from(initialFiles) : [];
        for (const file of filesToProcess) {
          try {
            const cls = await classifyClalExe(file);
            const item = { fileName: file.name, kind: cls.detectedKind, file, valueDate: cls.valueDate };
            // כל פרסר בנפרד — כשל ב-Apex (בריאות/חיים) לא יפיל את האחזקות, ולהפך.
            const partErrs = [];

            if (cls.holdings.length) {
              try {
                const xmlContents = cls.holdings.map((e) => ({ name: e.name, content: decodeXml(e.arrayBuffer) }));
                const { funds, taarichBitzua, sugMimshak } = parseXMLFiles(xmlContents);
                item.holdings = {
                  funds: funds || [],
                  taarichBitzua,
                  sugMimshak,
                  clientsCount: new Set((funds || []).map((f) => f.client?.id).filter(Boolean)).size,
                  shemYatzran: funds?.[0]?.companyName || "כלל",
                };
              } catch (e) { partErrs.push(`אחזקות: ${e.message}`); }
            }
            if (cls.health) {
              try { item.health = parseClalApex(cls.health, { fileName: file.name, kind: "health" }); }
              catch (e) { partErrs.push(`בריאות: ${e.message}`); }
            }
            if (cls.life) {
              try { item.life = parseClalApex(cls.life, { fileName: file.name, kind: "life" }); }
              catch (e) { partErrs.push(`חיים: ${e.message}`); }
            }
            // שגיאה מלאה רק אם שום מקור לא נטען; אחרת שגיאה חלקית (לא חוסמת).
            if (partErrs.length && !item.holdings && !item.health && !item.life) {
              item.error = partErrs.join(" · ");
              item.kind = CLAL_KIND.UNKNOWN;
            } else if (partErrs.length) {
              item.partialError = partErrs.join(" · ");
            }
            out.push(item);
          } catch (e) {
            out.push({ fileName: file.name, kind: CLAL_KIND.UNKNOWN, error: e.message });
          }
        }
        if (!cancelled) {
          setItems(out);
          setMonths(Object.fromEntries(
            out.map((it, i) => [i, deriveValueMonth(it.fileName, it.valueDate)])
          ));
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialFiles]);

  const handleConfirm = () => {
    // חודש-ערך חובה לכל קובץ שיש בו נתונים
    if (items.some((it, i) => clalItemHasData(it) && !months[i])) return;

    let holdingsClients = 0, holdingsFunds = 0, healthMembers = 0, lifeMembers = 0;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const valueDate = monthToValueDate(months[i]) || it.valueDate;
      if (it.holdings?.funds?.length) {
        const res = addHoldingsFunds(it.holdings.funds, {
          fileName: it.fileName,
          taarichBitzua: valueDate,
          shemYatzran: it.holdings.shemYatzran,
          sourceType: SOURCE_TYPE.PRAT,
        });
        holdingsClients += res.clientsTouched;
        holdingsFunds += res.fundsAdded;
      }
      if (it.health?.members?.length) {
        addClalImport(it.health.members, { fileName: it.fileName, kind: "health", taarichBitzua: valueDate, sourceType: SOURCE_TYPE.PRAT });
        healthMembers += it.health.members.length;
      }
      if (it.life?.members?.length) {
        addClalImport(it.life.members, { fileName: it.fileName, kind: "life", taarichBitzua: valueDate, sourceType: SOURCE_TYPE.PRAT });
        lifeMembers += it.life.members.length;
      }
    }
    const parts = [];
    if (holdingsFunds) parts.push(`${holdingsFunds} קרנות אחזקות (${holdingsClients} לקוחות)`);
    if (healthMembers) parts.push(`${healthMembers} פוליסות בריאות`);
    if (lifeMembers) parts.push(`${lifeMembers} פוליסות ביטוח חיים`);
    alert(parts.length ? `יובאו בהצלחה: ${parts.join(" · ")}` : "לא נמצאו נתונים לטעינה.");
    onClose();
  };

  if (loading) {
    return (
      <div style={S.overlay}>
        <div style={{ ...S.modal, width: 400, padding: 40, alignItems: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>🔄</div>
          <h3 style={{ margin: 0 }}>מחלץ קבצי כלל…</h3>
          <p style={{ color: "#64748b", marginTop: 8 }}>פותח ארכיוני .exe ומסווג אחזקות / בריאות</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.overlay}>
        <div style={{ ...S.modal, width: 400, padding: 32, alignItems: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
          <h3 style={{ margin: "0 0 16px 0", color: "#ef4444" }}>שגיאה בחילוץ</h3>
          <p style={{ color: "#64748b", fontSize: 13 }}>{error}</p>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={onClose}>סגור</button>
        </div>
      </div>
    );
  }

  const totals = items.reduce((a, it) => {
    a.hf += it.holdings?.funds?.length || 0;
    a.hp += it.health?.stats?.members || 0;
    a.lp += it.life?.stats?.members || 0;
    return a;
  }, { hf: 0, hp: 0, lp: 0 });

  const monthsReady = items.every((it, i) => !clalItemHasData(it) || months[i]);
  const canConfirm = (totals.hf || totals.hp || totals.lp) && monthsReady;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>ייבוא כלל חברה לביטוח</h2>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{items.length} קבצים · אחזקות (חיסכון) ובריאות</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 28, cursor: "pointer" }}>×</button>
        </div>

        <div style={S.content}>
          {items.map((it, i) => (
            <div key={i} style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", direction: "ltr", textAlign: "right", wordBreak: "break-all" }}>{it.fileName}</div>
                  {clalItemHasData(it) && (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: months[i] ? "#475569" : "#dc2626" }}>
                      📅 חודש הערך{!months[i] && " (חובה)"}:
                      <input
                        type="month"
                        value={months[i] || ""}
                        onChange={e => setMonths(prev => ({ ...prev, [i]: e.target.value }))}
                        style={{ padding: "3px 6px", borderRadius: 6, fontSize: 12, border: `1px solid ${months[i] ? "#cbd5e1" : "#fca5a5"}` }}
                      />
                    </label>
                  )}
                </div>
                {it.kind === CLAL_KIND.HOLDINGS && <span style={S.badge("#ecfeff", "#0e7490")}>📦 {CLAL_KIND_LABEL[it.kind]}</span>}
                {it.kind === CLAL_KIND.HEALTH && <span style={S.badge("#f5f3ff", "#6d28d9")}>🩺 בריאות</span>}
                {it.kind === CLAL_KIND.LIFE && <span style={S.badge("#eff6ff", "#1d4ed8")}>🛡️ ביטוח חיים</span>}
                {(it.kind === CLAL_KIND.UNKNOWN || it.error) && <span style={S.badge("#f1f5f9", "#64748b")}>❔ לא מזוהה</span>}
              </div>

              {it.error && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 8 }}>{it.error}</div>}

              {it.holdings?.funds?.length > 0 && (
                <div style={{ fontSize: 13, color: "#334155", marginTop: 8 }}>
                  <strong>{it.holdings.funds.length}</strong> קרנות · <strong>{it.holdings.clientsCount}</strong> לקוחות
                  {it.holdings.taarichBitzua && <> · תאריך ביצוע: {it.holdings.taarichBitzua}</>}
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>ייקלט לטאב "📦 אחזקות".</div>
                </div>
              )}

              {it.health?.members?.length > 0 && (
                <div style={{ fontSize: 13, color: "#334155", marginTop: 8 }}>
                  <strong>{it.health.stats.members}</strong> פוליסות · <strong>{it.health.stats.insured}</strong> מבוטחים
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {it.health.members.slice(0, 6).map((m) => (
                      <span key={m.rawId} style={{ background: "#f5f3ff", color: "#5b21b6", borderRadius: 8, padding: "3px 8px", fontSize: 12 }}>
                        {m.firstName || m.taz} ({m.funds.length} כיסויים)
                      </span>
                    ))}
                    {it.health.members.length > 6 && <span style={{ fontSize: 12, color: "#64748b" }}>+{it.health.members.length - 6}…</span>}
                  </div>
                </div>
              )}

              {it.life?.members?.length > 0 && (
                <div style={{ fontSize: 13, color: "#334155", marginTop: 8 }}>
                  <strong>{it.life.stats.members}</strong> פוליסות · <strong>{it.life.stats.insured}</strong> מבוטחים
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {it.life.members.slice(0, 6).map((m) => (
                      <span key={m.rawId} style={{ background: "#eff6ff", color: "#1d4ed8", borderRadius: 8, padding: "3px 8px", fontSize: 12 }}>
                        {m.firstName || m.taz} ({m.funds.length} כיסויים)
                      </span>
                    ))}
                    {it.life.members.length > 6 && <span style={{ fontSize: 12, color: "#64748b" }}>+{it.life.members.length - 6}…</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={S.footer}>
          <button style={{ ...S.btn, ...S.btnSecondary }} onClick={onClose}>ביטול</button>
          <button
            style={{ ...S.btn, ...(canConfirm ? S.btnPrimary : { background: "#cbd5e1", color: "white", cursor: "not-allowed" }) }}
            onClick={handleConfirm}
            disabled={!canConfirm}
            title={!monthsReady ? "יש לבחור חודש ערך לכל קובץ" : undefined}
          >
            אשר וטען ({totals.hf} קרנות · {totals.hp + totals.lp} פוליסות)
          </button>
        </div>
      </div>
    </div>
  );
}
